/** UTC wire ↔ display CalendarDateTime for date-time inputs. */

import { CalendarDateTime, parseTime, type Time } from "@internationalized/date";
import { joinDateTime, toDateOnly, toTimeOnly } from "@/lib/backtest-run";
import { parseUtcInstant } from "@/lib/dates";
import type { TimeZoneMode } from "@/stores/timezone";

const pad = (n: number) => String(n).padStart(2, "0");

function finiteOr(n: number, fallback: number): number {
  return Number.isFinite(n) ? n : fallback;
}

/** Normalize missing/NaN hour & minute to 0 (empty minutes → 00). */
export function normalizeClock(dt: CalendarDateTime): CalendarDateTime {
  return new CalendarDateTime(
    dt.year,
    dt.month,
    dt.day,
    finiteOr(dt.hour, 0),
    finiteOr(dt.minute, 0)
  );
}

/** Year/month/day/time look commit-ready (year must be 4 digits). */
export function isCompleteDateTime(dt: CalendarDateTime): boolean {
  return (
    dt.year >= 1000 &&
    dt.year <= 9999 &&
    dt.month >= 1 &&
    dt.month <= 12 &&
    dt.day >= 1 &&
    dt.day <= 31
  );
}

/** UTC wire `YYYY-MM-DD HH:MM:00` → calendar wall clock in the display zone. */
export function wireToDisplayCalendar(wire: string, mode: TimeZoneMode): CalendarDateTime | null {
  const t = parseUtcInstant(wire);
  if (!t) {
    const date = toDateOnly(wire);
    if (!date) return null;
    const time = toTimeOnly(wire) || "00:00";
    const [hh, mm] = time.split(":").map((x) => parseInt(x, 10));
    const [y, mo, d] = date.split("-").map((x) => parseInt(x, 10));
    if (!y || !mo || !d) return null;
    return new CalendarDateTime(y, mo, d, finiteOr(hh!, 0), finiteOr(mm!, 0));
  }
  if (mode === "local") {
    return new CalendarDateTime(
      t.getFullYear(),
      t.getMonth() + 1,
      t.getDate(),
      t.getHours(),
      t.getMinutes()
    );
  }
  return new CalendarDateTime(
    t.getUTCFullYear(),
    t.getUTCMonth() + 1,
    t.getUTCDate(),
    t.getUTCHours(),
    t.getUTCMinutes()
  );
}

/** Display-zone calendar → UTC wire `YYYY-MM-DD HH:MM:00`. Empty minutes → 00. */
export function displayCalendarToWire(dt: CalendarDateTime, mode: TimeZoneMode): string {
  const n = normalizeClock(dt);
  if (mode === "local") {
    const t = new Date(n.year, n.month - 1, n.day, n.hour, n.minute, 0, 0);
    return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:00`;
  }
  return joinDateTime(
    `${n.year}-${pad(n.month)}-${pad(n.day)}`,
    `${pad(n.hour)}:${pad(n.minute)}`
  );
}

/** Bound wire (UTC) → display calendar; date-only uses start/end of that day in display zone. */
export function wireBoundToDisplay(
  wire: string | undefined,
  mode: TimeZoneMode,
  endOfDay: boolean
): CalendarDateTime | undefined {
  if (!wire) return undefined;
  if (/[T ]\d{2}:\d{2}/.test(wire)) {
    return wireToDisplayCalendar(wire, mode) ?? undefined;
  }
  const date = toDateOnly(wire);
  if (!date) return undefined;
  // Date-only bounds are UTC calendar dates (API convention).
  const cal = wireToDisplayCalendar(joinDateTime(date, endOfDay ? "23:59" : "00:00"), "utc");
  if (!cal || mode === "utc") return cal ?? undefined;
  return wireToDisplayCalendar(displayCalendarToWire(cal, "utc"), mode) ?? undefined;
}

export function displayTimeValue(dt: CalendarDateTime | null): Time | null {
  if (!dt) return null;
  const n = normalizeClock(dt);
  try {
    return parseTime(`${pad(n.hour)}:${pad(n.minute)}:00`);
  } catch {
    return null;
  }
}

/** Current instant as UTC wire (same for both modes). */
export function nowWire(): string {
  const t = new Date();
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())} ${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}:00`;
}

/** "Today" in the display zone, keeping the current display time (or 00:00). */
export function todayWireKeepingTime(
  currentWire: string,
  mode: TimeZoneMode
): string {
  const current = wireToDisplayCalendar(currentWire, mode);
  const now = new Date();
  let y: number, mo: number, d: number;
  if (mode === "local") {
    y = now.getFullYear();
    mo = now.getMonth() + 1;
    d = now.getDate();
  } else {
    y = now.getUTCFullYear();
    mo = now.getUTCMonth() + 1;
    d = now.getUTCDate();
  }
  const hour = current ? finiteOr(current.hour, 0) : 0;
  const minute = current ? finiteOr(current.minute, 0) : 0;
  return displayCalendarToWire(new CalendarDateTime(y, mo, d, hour, minute), mode);
}

/** "Now" time on the currently selected display day (or full now if empty). */
export function nowTimeOnDisplayDay(
  currentWire: string,
  mode: TimeZoneMode
): string {
  const current = wireToDisplayCalendar(currentWire, mode);
  if (!current) return nowWire();
  const now = new Date();
  let hour: number, minute: number;
  if (mode === "local") {
    hour = now.getHours();
    minute = now.getMinutes();
  } else {
    hour = now.getUTCHours();
    minute = now.getUTCMinutes();
  }
  return displayCalendarToWire(
    new CalendarDateTime(current.year, current.month, current.day, hour, minute),
    mode
  );
}
